import {
    BackendType,
    convertDateArrayFromDate,
    convertDateFromDateArray,
    FrontendType,
    ObjectType,
} from '@starjack/utils';
import { MultiLangString, StarjackDate } from '@starjack/utils';
import { parse } from 'date-fns';
import { Environment } from '../../environment';
import { Money } from '../../money';

export const PC_DEFAULT_DATE_START = parse(
    '2000-01-01',
    'yyyy-MM-dd',
    new Date()
);
export const PC_DEFAULT_DATE_END = parse(
    '2100-01-01',
    'yyyy-MM-dd',
    new Date()
);

export type IntegrationConfiguration = {
    barcode: boolean;
    internalProduction: boolean;
    keycard: boolean;
    pickup: boolean;
    mobileFlow: boolean;
};

export const allEnvironments = [
    Environment.PORTAL,
    Environment.INTEGRATION,
    Environment.B2B,
    Environment.HOSPITALITY,
];

export enum ProductType {
    SEASON = 'SEASON',
    DAY = 'DAY',
}

type ProductInfo = {
    duration: number;
    name: MultiLangString;
    type: ProductType;
};

export type ProductConfiguration = {
    enabled: boolean;
    id: string;
    disableStartDateToday: boolean;
    displayEndDate: boolean;
    startDateLabel?: StarjackDate;
    allowZeroPrice: boolean;
    keycardCodes: string[];
    liftOperatorId: string;
    conditionLinkIds: string[];
    integrationConfiguration: IntegrationConfiguration;
    portalConfiguration: IntegrationConfiguration;
    b2bConfiguration: IntegrationConfiguration;
    hospitalityConfiguration: IntegrationConfiguration;
    product?: ProductInfo;
    productId: string;
    infoTextDE: string;
    infoTextEN: string;
    pickupInfoTextDE: string;
    pickupInfoTextEN: string;
    barcodeInfoTextDE: string;
    barcodeInfoTextEN: string;
    filterTagsDE: string;
    filterTagsEN: string;
    sortKey: number;
    maxTicketsPerStartDay: number;
    contingentsThreshold: number;
    tags: string[];
    validityRange: {
        from: StarjackDate;
        to: StarjackDate;
    };
    startDateValidityRange: {
        from: StarjackDate;
        to: StarjackDate;
    };
    endDate?: StarjackDate;
    durationOverride: number;
    productTypeOverride: ProductType;
    birthdayRequired: boolean;
};

export type BackendProductConfiguration = {
    enabled: boolean;
    allowZeroPrice: boolean;
    id: string;
    disableStartDateToday: boolean;
    displayEndDate: boolean;
    startDateLabel?: number[];
    integrationConfiguration: IntegrationConfiguration;
    keycardCodes: string[];
    liftOperatorId: string;
    portalConfiguration: IntegrationConfiguration;
    b2bConfiguration: IntegrationConfiguration;
    hospitalityConfiguration: IntegrationConfiguration;
    pickupInfoText: MultiLangString;
    barcodeInfoText: MultiLangString;
    product?: ProductInfo;
    productId: string;
    sortKey: number;
    maxTicketsPerStartDay: number;
    conditionLinkIds: string[];
    contingentsThreshold: number;
    tags: string[];
    infoText: MultiLangString;
    filterTags: MultiLangString;
    validityRange: {
        from: number[];
        to: number[];
    };
    startDateValidityRange: {
        from: number[];
        to: number[];
    };
    endDate?: number[];
    durationOverride: number;
    productTypeOverride: ProductType;
    birthdayRequired: boolean;
};

export const mapProductConfigurationToFrontend = (
    bPc: BackendProductConfiguration
): ProductConfiguration => {
    return {
        ...bPc,
        startDateLabel: convertDateFromDateArray(bPc.startDateLabel),
        pickupInfoTextDE: bPc.pickupInfoText?.de,
        pickupInfoTextEN: bPc.pickupInfoText?.en,
        barcodeInfoTextDE: bPc.barcodeInfoText?.de,
        barcodeInfoTextEN: bPc.barcodeInfoText?.en,
        infoTextDE: bPc.infoText?.de,
        infoTextEN: bPc.infoText?.en,
        filterTagsDE: bPc.filterTags?.de,
        filterTagsEN: bPc.filterTags?.en,
        validityRange: {
            from: convertDateFromDateArray(bPc.validityRange.from),
            to: convertDateFromDateArray(bPc.validityRange.to),
        },
        startDateValidityRange: {
            from: convertDateFromDateArray(bPc.startDateValidityRange.from),
            to: convertDateFromDateArray(bPc.startDateValidityRange.to),
        },
        endDate: convertDateFromDateArray(bPc.endDate),
    };
};

export const mapProductConfigurationToBackend = (
    p: ProductConfiguration
): BackendProductConfiguration => {
    // PC_DEFAULT_DATE_START
    // PC_DEFAULT_DATE_END

    const configuration = {
        ...p,
        infoText: {
            de: p.infoTextDE,
            en: p.infoTextEN,
        },
        filterTags: {
            de: p.filterTagsDE,
            en: p.filterTagsEN,
        },
        pickupInfoText: {
            de: p.pickupInfoTextDE,
            en: p.pickupInfoTextEN,
        },
        barcodeInfoText: {
            de: p.barcodeInfoTextDE,
            en: p.barcodeInfoTextEN,
        },
        productTypeOverride: p.productTypeOverride || null,
        validityRange: {
            from: convertDateArrayFromDate(p.validityRange.from),
            to: convertDateArrayFromDate(p.validityRange.to),
        },

        startDateValidityRange: {
            from: convertDateArrayFromDate(
                p.startDateValidityRange.from || PC_DEFAULT_DATE_START
            ),
            to: convertDateArrayFromDate(
                p.startDateValidityRange.to || PC_DEFAULT_DATE_END
            ),
        },
        endDate: p.endDate ? convertDateArrayFromDate(p.endDate) : undefined,
        startDateLabel: p.startDateLabel
            ? convertDateArrayFromDate(p.startDateLabel)
            : undefined,
    } as BackendProductConfiguration;

    // TODO: refactor proper typing
    delete (configuration as unknown as ProductConfiguration).infoTextDE;
    delete (configuration as unknown as ProductConfiguration).infoTextEN;
    delete (configuration as unknown as ProductConfiguration).filterTagsDE;
    delete (configuration as unknown as ProductConfiguration).filterTagsEN;
    delete (configuration as unknown as ProductConfiguration).pickupInfoTextDE;
    delete (configuration as unknown as ProductConfiguration).pickupInfoTextDE;
    delete (configuration as unknown as ProductConfiguration).barcodeInfoTextDE;
    delete (configuration as unknown as ProductConfiguration).barcodeInfoTextEN;

    return configuration;
};

export type ProductPriceData<T extends ObjectType = FrontendType> = {
    id: string;
    validity: { from: StarjackDate<T>; to: StarjackDate<T> };
    price: Money;
    extra: Record<string, string>;
};

export type ProductPerson<T extends ObjectType = FrontendType> = {
    name: { de: string; en?: string };
    birthday: { from: StarjackDate<T>; to: StarjackDate<T> };
    prices: ProductPriceData<T>[];
};

export type Product<T extends ObjectType = FrontendType> = {
    shortProductId: string;
    name: MultiLangString;
    type: string;
    duration: number;
    photoRequired: boolean;
    persons: ProductPerson<T>[];
    tags: string[];
    sortKey: number;
    infoText: Record<string, string>;
    filterTags: string[];
    maxTicketsPerStartDay: number | null;
    contingentsThreshold: number | null;
    externalCode: string | null;
    keycardCodes: string[];
    portalConfiguration: Record<string, boolean>;
    integrationConfiguration: Record<string, boolean>;
    b2bConfiguration: Record<string, boolean>;
    hospitalityConfiguration: Record<string, boolean>;
    mobileConfiguration: Record<string, boolean>;
};

export const mapProductToFrontend = (
    product: Product<BackendType>
): Product => {
    return {
        ...product,
        persons: product.persons.map((person) => ({
            ...person,
            birthday: {
                from: convertDateFromDateArray(
                    person.birthday.from as unknown as number[]
                ),
                to: convertDateFromDateArray(
                    person.birthday.to as unknown as number[]
                ),
            },
            prices: person.prices.map((price) => ({
                ...price,
                validity: {
                    from: convertDateFromDateArray(price.validity.from),
                    to: convertDateFromDateArray(price.validity.to),
                },
            })),
        })),
    } as unknown as Product;
};

export type PackageTicket<T extends ObjectType = BackendType> = Omit<
    Product<T>,
    'persons'
> & {
    productId: string;
    price: Money;
    validity: { from: StarjackDate<T>; to: StarjackDate<T> };
    issueValidity: Array<{ from: StarjackDate<T>; to: StarjackDate<T> }>;
    persons: Array<{
        name: { de: string; en?: string };
        birthday: { from: StarjackDate<T>; to: StarjackDate<T> } | null;
        quantity: number;
        extra: Record<string, unknown>;
    }>;
};

export const mapPackageTicketToFrontend = (
    ticket: PackageTicket<BackendType>
): Product => {
    const validity = {
        from: convertDateFromDateArray(
            ticket.validity.from as unknown as number[]
        ),
        to: convertDateFromDateArray(
            ticket.validity.to as unknown as number[]
        ),
    };

    return {
        ...ticket,
        persons: ticket.persons.map((person) => ({
            name: person.name,
            birthday: person.birthday
                ? {
                      from: convertDateFromDateArray(
                          person.birthday.from as unknown as number[]
                      ),
                      to: convertDateFromDateArray(
                          person.birthday.to as unknown as number[]
                      ),
                  }
                : null,
            prices: [
                {
                    id: ticket.productId,
                    validity,
                    price: ticket.price,
                    extra: person.extra as Record<string, string>,
                },
            ],
        })),
    } as unknown as Product;
};
